import { clamp, distance, lerp } from '../utils/math';
import { EMPTY_MOVEMENT, type MovementData, type PoseFrame } from './types';

export class MovementAnalyzer {
  private previous: PoseFrame | null = null;
  private smoothed: MovementData = { ...EMPTY_MOVEMENT };

  analyze(frame: PoseFrame): MovementData {
    const p = frame.points;
    const lw = p.left_wrist, rw = p.right_wrist;
    const ls = p.left_shoulder, rs = p.right_shoulder;
    const lh = p.left_hip, rh = p.right_hip;
    if (!lw || !rw || !ls || !rs || !lh || !rh) return this.smoothed;
    const dt = this.previous ? Math.max(16, frame.timestamp - this.previous.timestamp) / 1000 : 1 / 30;
    const prev = this.previous?.points;
    const speed = (name: string) => prev?.[name] ? clamp(distance(p[name], prev[name]) / dt / 1.8) : 0;
    const leftHandSpeed = speed('left_wrist');
    const rightHandSpeed = speed('right_wrist');
    const centre = { x: (lh.x + rh.x + ls.x + rs.x) / 4, y: (lh.y + rh.y + ls.y + rs.y) / 4 };
    const oldCentre = prev && prev.left_hip ? {
      x: (prev.left_hip.x + prev.right_hip.x + prev.left_shoulder.x + prev.right_shoulder.x) / 4,
      y: (prev.left_hip.y + prev.right_hip.y + prev.left_shoulder.y + prev.right_shoulder.y) / 4,
    } : centre;
    const dx = clamp((centre.x - oldCentre.x) / dt * 3, -1, 1);
    const dy = clamp((centre.y - oldCentre.y) / dt * 3, -1, 1);
    const span = distance(lw, rw);
    const shoulderWidth = Math.max(.08, distance(ls, rs));
    const expansion = clamp(span / (shoulderWidth * 3));
    const vertical = clamp(1 - (lw.y + rw.y) / 2);
    const energy = clamp((leftHandSpeed + rightHandSpeed) * .32 + Math.abs(dx) * .12 + Math.abs(dy) * .12 + expansion * .12);
    let directionLabel = energy < .08 ? '慢' : energy > .62 ? '快' : '律動';
    if (Math.abs(dy) > .34) directionLabel = dy < 0 ? '向上' : '向下';
    else if (Math.abs(dx) > .34) directionLabel = dx < 0 ? '向左' : '向右';
    else if (expansion > .78) directionLabel = '大動作';
    else if (expansion < .28) directionLabel = '小動作';
    const raw = { movementEnergy: energy, leftHandSpeed, rightHandSpeed, verticalPosition: vertical,
      horizontalDirection: dx, verticalDirection: -dy, bodyExpansion: expansion,
      gestureScale: clamp(span / .8), directionLabel };
    const alpha = .22;
    this.smoothed = Object.fromEntries(Object.entries(raw).map(([key, value]) =>
      [key, typeof value === 'number' ? lerp((this.smoothed as any)[key] ?? value, value, alpha) : value])) as unknown as MovementData;
    this.previous = frame;
    return this.smoothed;
  }

  reset() { this.previous = null; this.smoothed = { ...EMPTY_MOVEMENT }; }
}
